import React, { useLayoutEffect, useRef, useState, cloneElement, isValidElement } from 'react';
import Tooltip from './Tooltip';
import './CellEllipsis.css';

function useTruncationWatch(ref, deps) {
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setTruncated(false);
      return undefined;
    }
    const measure = () => {
      // Treat any horizontal overflow (even sub‑pixel) as truncated so we
      // always show the tooltip when CSS shows an ellipsis.
      const next = el.scrollWidth - el.clientWidth > 0.5;
      setTruncated((prev) => (prev !== next ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps list provided by caller
  }, deps);

  return truncated;
}

/**
 * Plain text cell: CSS ellipsis; tooltip with full text only when visually truncated.
 */
export function CellEllipsis({ text, className }) {
  const display = text != null ? String(text) : '';
  const innerRef = useRef(null);
  const truncated = useTruncationWatch(innerRef, [display]);
  const shouldTooltip = truncated || display.length > 12;

  const inner = (
    <span ref={innerRef} className={`cell-ellipsis-inner ${className || ''}`.trim()}>
      {display}
    </span>
  );

  if (!display) {
    return <div className="overflow-cell-wrap">{inner}</div>;
  }

  return (
    <div className="overflow-cell-wrap">
      {shouldTooltip ? (
        <Tooltip text={display} position="bottom" bypassLengthGate>
          {inner}
        </Tooltip>
      ) : (
        inner
      )}
    </div>
  );
}

/**
 * Single child (e.g. button) with ellipsis; tooltip when child content is truncated horizontally.
 * Pass full string in tooltipText for hover.
 */
export function OverflowCell({ tooltipText, children, className }) {
  const tt = tooltipText != null ? String(tooltipText) : '';
  const innerRef = useRef(null);
  const shouldTooltip = tt.length > 12;

  const setRefs = (node) => {
    innerRef.current = node;
    if (!isValidElement(children)) return;
    const { ref: r } = children;
    if (typeof r === 'function') r(node);
    else if (r) r.current = node;
  };

  const truncated = useTruncationWatch(innerRef, [tt]);

  const inner = isValidElement(children) ? (
    cloneElement(children, {
      ref: setRefs,
      className: ['cell-ellipsis-inner', children.props.className].filter(Boolean).join(' ')
    })
  ) : (
    <span ref={innerRef} className="cell-ellipsis-inner">
      {children}
    </span>
  );

  return (
    <div className={`overflow-cell-wrap ${className || ''}`.trim()}>
      {tt && (truncated || shouldTooltip) ? (
        <Tooltip text={tt} position="bottom" bypassLengthGate>
          {inner}
        </Tooltip>
      ) : (
        inner
      )}
    </div>
  );
}
